/**
 * Desktop & Dock settings — the Dock's side (also set by dragging the Dock
 * to an edge), magnification on / off, and the desktop picture (a built-in one
 * or the user's own). Saved per user, per application, on the server.
 */
import { Check, ImagePlus, Trash2, X } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../../../testHooks'
import { S, iconButton } from '../../nav/tokens'
import type { ShellNavModel } from '../../nav/useShellNav'
import { WALLPAPERS, prepareWallpaper, wallpaperCss } from './wallpapers'

export type DockSide = 'bottom' | 'left' | 'right'
/** The Dock size slider's range (px per tile). */
export const DOCK_SIZE_MIN = 36
export const DOCK_SIZE_MAX = 80
export const DOCK_SIZE_DEFAULT = 50

export const DOCK_SIDES: { id: DockSide; label: string }[] = [
  { id: 'left', label: 'Left' }, { id: 'bottom', label: 'Bottom' }, { id: 'right', label: 'Right' },
]

export function MacSettings({
  model, accent, dockSide, setDockSide, dockSize, setDockSize, magnify, setMagnify, picture, setPicture, onClose,
}: {
  model: ShellNavModel
  accent: string
  dockSide: DockSide
  setDockSide: (s: DockSide) => void
  dockSize: number
  setDockSize: (px: number) => void
  magnify: boolean
  setMagnify: (on: boolean) => void
  picture: string
  setPicture: (id: string) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const back = useRef<HTMLElement | null>(typeof document !== 'undefined' ? document.activeElement as HTMLElement : null)

  useEffect(() => {
    model.loadWallpaper()
    ref.current?.querySelector<HTMLElement>('button, input')?.focus()
    const ret = back.current
    return () => { ret?.focus?.() }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose() }
    if (e.key !== 'Tab') return
    // Keep focus inside the dialog.
    const els = [...(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') || [])]
    if (!els.length) return
    const i = els.indexOf(document.activeElement as HTMLElement)
    if (e.shiftKey && i <= 0) { e.preventDefault(); els[els.length - 1].focus() }
    else if (!e.shiftKey && i === els.length - 1) { e.preventDefault(); els[0].focus() }
  }

  const choose = async (file: File | undefined) => {
    if (!file) return
    setBusy(true)
    setError('')
    try {
      const data = await prepareWallpaper(file)
      await model.setWallpaper(data)
      setPicture('custom')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.warn('[desktop] picture not saved', err)
      setError(msg || 'The picture could not be saved.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }
  const remove = async () => {
    setBusy(true)
    setError('')
    try {
      await model.setWallpaper(null)
      if (picture === 'custom') setPicture('theme')
    } catch (err) {
      console.warn('[desktop] picture not removed', err)
      setError(err instanceof Error ? err.message : 'The picture could not be removed.')
    } finally {
      setBusy(false)
    }
  }

  const sectionH = { margin: '14px 0 8px', fontSize: 14.5, fontWeight: 600 } as const
  const image = model.wallpaper.image
  const swatches = [
    ...WALLPAPERS.map((w) => ({ id: w.id, label: w.label, css: w.css(accent) })),
    ...(image ? [{ id: 'custom', label: 'Your picture', css: wallpaperCss('custom', accent, image) }] : []),
  ]

  if (typeof document === 'undefined') return null
  return createPortal(
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, zIndex: 9460, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby="tw-mac-settings-h" onKeyDown={onKey}
        data-tw-dialog="shell-mac-settings"
        style={{ width: 'min(620px, 100%)', maxHeight: '90vh', overflow: 'auto', borderRadius: 14, padding: 18,
          background: S.raised, color: S.text, border: `1px solid ${S.border}`, fontFamily: S.font,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 id="tw-mac-settings-h" style={{ margin: 0, fontSize: 17, fontWeight: 600, flex: 1 }}>Desktop &amp; Dock</h2>
          <button type="button" className="tw-sh-btn" onClick={onClose} aria-label="Close" style={iconButton(34)}>
            <X size={16} aria-hidden />
          </button>
        </div>

        <h3 style={sectionH} id="tw-mac-dock-pos">Position on screen</h3>
        <div role="radiogroup" aria-labelledby="tw-mac-dock-pos" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
          onKeyDown={(e) => {
            const i = DOCK_SIDES.findIndex((d) => d.id === dockSide)
            const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
            if (!step) return
            e.preventDefault()
            const next = DOCK_SIDES[(i + step + DOCK_SIDES.length) % DOCK_SIDES.length]
            setDockSide(next.id)
            ;(e.currentTarget.querySelector(`[data-side="${next.id}"]`) as HTMLElement | null)?.focus()
          }}>
          {DOCK_SIDES.map((d) => {
            const on = d.id === dockSide
            return (
              <button key={d.id} type="button" role="radio" aria-checked={on} data-side={d.id} tabIndex={on ? 0 : -1}
                onClick={() => setDockSide(d.id)} {...tw('action', `shell-mac-dock-side:${d.id}`, { selected: on })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 40, padding: '0 16px',
                  borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14.5, color: S.text,
                  background: on ? S.accentWeak : 'transparent', border: `${on ? 2 : 1}px solid ${on ? S.accent : S.border}` }}>
                {on && <Check size={14} color={S.accent} aria-hidden />}{d.label}
              </button>
            )
          })}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: S.text2 }}>You can also drag the Dock to the left, bottom or right edge.</p>

        <label style={{ display: 'block', marginTop: 14, fontSize: 14.5 }}>
          <span style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontWeight: 600 }}>Size</span>
            <span style={{ color: S.text2, fontSize: 13.5 }}>{dockSize}px</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: S.text2 }}>
            Small
            <input type="range" min={DOCK_SIZE_MIN} max={DOCK_SIZE_MAX} step={2} value={dockSize}
              onChange={(e) => setDockSize(Number(e.target.value))} aria-label="Dock size"
              {...tw('field', 'shell-mac-dock-size')} style={{ flex: 1, accentColor: accent }} />
            Large
          </span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, cursor: 'pointer', fontSize: 14.5 }}>
          <input type="checkbox" role="switch" checked={magnify} onChange={(e) => setMagnify(e.target.checked)}
            {...tw('field', 'shell-mac-magnify')} style={{ width: 18, height: 18, accentColor: accent }} />
          Magnification — Dock icons grow as the pointer passes over them
        </label>

        <h3 style={sectionH} id="tw-mac-pic">Desktop picture</h3>
        <div role="radiogroup" aria-labelledby="tw-mac-pic"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10 }}>
          {swatches.map((w) => {
            const on = w.id === picture
            return (
              <button key={w.id} type="button" role="radio" aria-checked={on} onClick={() => setPicture(w.id)}
                {...tw('action', `shell-mac-wallpaper:${w.id}`, { selected: on })}
                style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 6, borderRadius: 10, cursor: 'pointer',
                  fontFamily: 'inherit', color: S.text, textAlign: 'left',
                  background: on ? S.accentWeak : 'transparent', border: `${on ? 2 : 1}px solid ${on ? S.accent : S.border}` }}>
                <span aria-hidden style={{ display: 'block', height: 64, borderRadius: 6, background: w.css, border: `1px solid ${S.border}` }} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13.5 }}>
                  {on && <Check size={13} color={S.accent} aria-hidden />}{w.label}
                </span>
              </button>
            )
          })}
        </div>
        {model.wallpaper.supported ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
              onChange={(e) => { void choose(e.target.files?.[0]) }} aria-label="Choose a desktop picture" />
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
              {...tw('action', 'shell-mac-wallpaper-choose')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 38, padding: '0 14px',
                borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14.5, color: '#fff',
                background: `color-mix(in srgb, ${accent} 82%, #000)`, border: 'none', opacity: busy ? 0.6 : 1 }}>
              <ImagePlus size={16} aria-hidden /> {busy ? 'Saving…' : 'Choose a picture…'}
            </button>
            {image && (
              <button type="button" disabled={busy} onClick={() => { void remove() }}
                {...tw('action', 'shell-mac-wallpaper-remove')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 38, padding: '0 14px',
                  borderRadius: 9, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14.5, color: S.text,
                  background: 'transparent', border: `1px solid ${S.border}` }}>
                <Trash2 size={15} aria-hidden /> Remove my picture
              </button>
            )}
          </div>
        ) : (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: S.text2 }}>This server keeps only the built-in pictures.</p>
        )}
        {error && <p role="alert" style={{ margin: '8px 0 0', fontSize: 13.5, color: S.error }}>{error}</p>}
      </div>
    </div>,
    document.body,
  )
}
